import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  InputNumber, 
  Select, 
  Button, 
  Row, 
  Col, 
  Typography, 
  Divider, 
  Space,
  Table,
  Statistic,
  Alert,
  Collapse,
  DatePicker
} from 'antd';
import { CalculatorOutlined, EnvironmentOutlined, BuildOutlined, SafetyOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const TopsoilCalculator = () => {
  const [form] = Form.useForm();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const soilTypeOptions = [
    { label: '黑土（40 cm，30-50 cm）', value: 0.40 },
    { label: '黄土（25 cm，20-30 cm）', value: 0.25 },
    { label: '红壤（20 cm，15-25 cm）', value: 0.20 },
    { label: '紫色土（25 cm，20-30 cm）', value: 0.251 },
    { label: '棕壤（30 cm，25-35 cm）', value: 0.30 },
    { label: '褐土（25 cm，20-30 cm）', value: 0.252 },
    { label: '潮土（30 cm，25-35 cm）', value: 0.301 },
    { label: '水稻土（20 cm，15-25 cm）', value: 0.201 },
    { label: '灰钙土（20 cm，15-25 cm）', value: 0.202 },
    { label: '荒漠土（15 cm，10-20 cm）', value: 0.15 },
    { label: '石质土（10 cm，5-15 cm）', value: 0.10 },
  ];

  const siteTypeOptions = [
    { label: '农业用地复垦（50 cm，40-60 cm）', value: 0.50 },
    { label: '林草地复垦（30 cm，20-40 cm）', value: 0.30 },
    { label: '弃渣场复垦（40 cm，30-50 cm）', value: 0.40 },
    { label: '取土场复垦（30 cm，20-40 cm）', value: 0.301 },
    { label: '施工临时占地复垦（20 cm，15-25 cm）', value: 0.20 },
    { label: '工业场地复垦（30 cm，20-40 cm）', value: 0.302 },
    { label: '临时堆土场（20 cm，15-25 cm）', value: 0.201 },
    { label: '边坡绿化（15 cm，10-20 cm）', value: 0.15 },
    { label: '绿化带（30 cm，20-40 cm）', value: 0.303 },
    { label: '道路绿化（25 cm，20-30 cm）', value: 0.25 },
  ];

  const storageTypeOptions = [
    { label: '短期堆存（<1年，3-4 m，0.8-0.9）', value: JSON.stringify({ height: 3.5, coefficient: 0.85 }) },
    { label: '中期堆存（1-3年，3-5 m，0.7-0.85）', value: JSON.stringify({ height: 4, coefficient: 0.775 }) },
    { label: '长期堆存（>3年，2-3 m，0.7-0.8）', value: JSON.stringify({ height: 2.5, coefficient: 0.75 }) },
  ];

  const calculateTopsoil = (values) => {
    const { 
      stripArea, 
      soilThickness, 
      reuseArea, 
      reuseThickness, 
      lossCoefficient,
      storageHeight,
      storageCoefficient,
      stripDate,
      reuseDate
    } = values;

    const storageTypeObj = {
      height: storageHeight,
      coefficient: storageCoefficient
    };

    const V1 = stripArea * 10000 * soilThickness;
    const V2 = reuseArea * 10000 * reuseThickness * (1 + lossCoefficient);
    const deltaV = V1 - V2;

    let storageArea = 0;
    let storageAreaMu = 0;
    let finalStorageHeight = 0;

    if (deltaV > 0) {
      finalStorageHeight = storageTypeObj.height;
      storageArea = deltaV / (finalStorageHeight * storageTypeObj.coefficient);
      storageAreaMu = storageArea / 666.67;
    }

    const surplusRatio = deltaV > 0 ? (deltaV / V2) * 100 : 0;
    const deficitRatio = deltaV < 0 ? (Math.abs(deltaV) / V2) * 100 : 0;

    let storageDuration = 0;
    if (stripDate && reuseDate) {
      const stripDateObj = stripDate.toDate();
      const reuseDateObj = reuseDate.toDate();
      storageDuration = (reuseDateObj - stripDateObj) / (1000 * 60 * 60 * 24 * 365);
    }

    return {
      V1, V2, deltaV,
      surplusRatio, deficitRatio,
      storageArea, storageAreaMu, storageHeight: finalStorageHeight,
      storageDuration,
      stripArea, reuseArea,
      soilThickness, reuseThickness,
      lossCoefficient
    };
  };

  const getTopsoilStatus = (result) => {
    if (result.deltaV > 0) {
      return {
        status: 'success',
        title: '表土盈余',
        description: `盈余 ${result.deltaV.toFixed(2)} m³，可暂存于临时堆场，堆存面积 ${result.storageArea.toFixed(2)} m²（${result.storageAreaMu.toFixed(2)} 亩）`,
        color: 'green'
      };
    } else if (result.deltaV < 0) {
      return {
        status: 'warning',
        title: '表土缺口',
        description: `缺口 ${Math.abs(result.deltaV).toFixed(2)} m³，需从其他合适区域调配`,
        color: 'orange'
      };
    } else {
      return {
        status: 'info',
        title: '表土平衡',
        description: '表土供需平衡，无需外调或临时堆存',
        color: 'blue'
      };
    }
  };

  const getOptimizationSuggestions = (result) => {
    const suggestions = [];

    if (result.deltaV > 0) {
      suggestions.push({
        type: 'success',
        title: '表土资源充足',
        description: `表土盈余 ${result.deltaV.toFixed(2)} m³，建议规划临时堆存场地，堆存面积 ${result.storageArea.toFixed(2)} m²（${result.storageAreaMu.toFixed(2)} 亩），堆存高度 ${result.storageHeight.toFixed(1)} m`
      });
      if (result.storageDuration > 3) {
        suggestions.push({
          type: 'warning',
          title: '堆存时间过长',
          description: `表土堆存时间 ${result.storageDuration.toFixed(1)} 年，超过建议的3年，建议加快回用进度或采取额外的土壤保护措施`
        });
      }
    } else if (result.deltaV < 0) {
      suggestions.push({
        type: 'warning',
        title: '表土资源不足',
        description: `表土缺口 ${Math.abs(result.deltaV).toFixed(2)} m³，需从其他区域调配表土，建议提前规划外调方案`
      });
      suggestions.push({
        type: 'info',
        title: '外调表土来源建议',
        description: '可从临近项目的临时堆存场地、其他工地的表土剥离区域或专门的表土供应商处获取'
      });
    } else {
      suggestions.push({
        type: 'success',
        title: '表土资源平衡',
        description: '表土供需平衡，无需外调或临时堆存，方案合理'
      });
    }

    if (result.lossCoefficient > 0.08) {
      suggestions.push({
        type: 'info',
        title: '损耗系数偏高',
        description: '当前损耗系数较高，建议优化运输和堆存方案，减少表土损耗'
      });
    }

    return suggestions;
  };

  const handleCalculate = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      const stripDate = values.stripDate ? dayjs(values.stripDate) : null;
      const reuseDate = values.reuseDate ? dayjs(values.reuseDate) : null;
      
      const result = calculateTopsoil({
        ...values,
        stripDate,
        reuseDate
      });
      setResult(result);
    } catch (error) {
      console.error('计算失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStorageTypeChange = (value) => {
    if (value) {
      const storageTypeObj = JSON.parse(value);
      form.setFieldsValue({
        storageHeight: storageTypeObj.height,
        storageCoefficient: storageTypeObj.coefficient
      });
    }
  };

  const handleReset = () => {
    form.resetFields();
    setResult(null);
  };

  const resultColumns = [
    {
      title: '参数',
      dataIndex: 'parameter',
      key: 'parameter',
      width: '40%',
    },
    {
      title: '数值',
      dataIndex: 'value',
      key: 'value',
      width: '30%',
      render: (value) => typeof value === 'number' ? value.toFixed(2) : value,
    },
    {
      title: '单位',
      dataIndex: 'unit',
      key: 'unit',
      width: '30%',
    },
  ];

  const resultData = result ? [
    { key: '1', parameter: '可剥离表土总量', value: result.V1, unit: 'm³' },
    { key: '2', parameter: '需回用表土总量', value: result.V2, unit: 'm³' },
    { key: '3', parameter: '表土盈余/缺口量', value: result.deltaV, unit: 'm³' },
    { key: '4', parameter: '盈余/缺口比例', value: result.deltaV > 0 ? result.surplusRatio : result.deficitRatio, unit: '%' },
    { key: '5', parameter: '临时堆存场地面积', value: result.storageArea, unit: 'm²' },
    { key: '6', parameter: '临时堆存场地面积', value: result.storageAreaMu, unit: '亩' },
  ] : [];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
          <CalculatorOutlined /> 表土剥离与回用计算器
        </Title>
        <Text type="secondary">
          计算项目区表土剥离量和需回用表土量，合理规划表土资源的利用
        </Text>
      </div>

      <Alert
        message="计算依据"
        description="本工具依据 GB/T 45107-2024《表土剥离及其再利用技术要求》、TD/T 1036-2013《土地复垦质量控制标准》、《土地复垦条例》进行计算。"
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <Card title="输入参数" variant="borderless" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical">
              <Collapse defaultActiveKey={['1', '2', '3', '4']}>
                <Panel header={<><EnvironmentOutlined /> 剥离区域信息</>} key="1">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="表土剥离区域面积（hm²）"
                        name="stripArea"
                        rules={[{ required: true, message: '请输入表土剥离区域面积' }]}
                      >
                        <InputNumber
                          min={0.1}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入表土剥离区域面积"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="土壤类型"
                        name="soilThickness"
                        rules={[{ required: true, message: '请选择土壤类型' }]}
                      >
                        <Select
                          placeholder="请选择土壤类型"
                          options={soilTypeOptions}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>

                <Panel header={<><BuildOutlined /> 回用区域信息</>} key="2">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="需覆土场地类型"
                        name="reuseThickness"
                        rules={[{ required: true, message: '请选择需覆土场地类型' }]}
                      >
                        <Select
                          placeholder="请选择需覆土场地类型"
                          options={siteTypeOptions}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="需覆土场地面积（hm²）"
                        name="reuseArea"
                        rules={[{ required: true, message: '请输入需覆土场地面积' }]}
                      >
                        <InputNumber
                          min={0.1}
                          precision={2}
                          style={{ width: '100%' }}
                          placeholder="请输入需覆土场地面积"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>

                <Panel header={<><SafetyOutlined /> 损耗参数</>} key="3">
                  <Form.Item
                    label="堆存类型"
                    name="storageType"
                  >
                    <Select
                      placeholder="请选择堆存类型"
                      options={storageTypeOptions}
                      onChange={handleStorageTypeChange}
                    />
                  </Form.Item>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        label="表土损耗系数（%）"
                        name="lossCoefficient"
                        rules={[{ required: true, message: '请输入表土损耗系数' }]}
                        initialValue={0.08}
                      >
                        <InputNumber
                          min={0.05}
                          max={0.10}
                          precision={3}
                          step={0.01}
                          style={{ width: '100%' }}
                          placeholder="请输入表土损耗系数"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        label="堆存高度（m）"
                        name="storageHeight"
                        rules={[{ required: true, message: '请输入堆存高度' }]}
                        initialValue={4}
                      >
                        <InputNumber
                          min={2}
                          max={5}
                          precision={1}
                          step={0.5}
                          style={{ width: '100%' }}
                          placeholder="请输入堆存高度"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        label="堆存系数"
                        name="storageCoefficient"
                        rules={[{ required: true, message: '请输入堆存系数' }]}
                        initialValue={0.8}
                      >
                        <InputNumber
                          min={0.7}
                          max={0.9}
                          precision={2}
                          step={0.05}
                          style={{ width: '100%' }}
                          placeholder="请输入堆存系数"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>

                <Panel header={<><ClockCircleOutlined /> 时间参数</>} key="4">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="表土剥离时间"
                        name="stripDate"
                      >
                        <DatePicker 
                          style={{ width: '100%' }}
                          placeholder="请选择表土剥离时间"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="表土回用时间"
                        name="reuseDate"
                      >
                        <DatePicker 
                          style={{ width: '100%' }}
                          placeholder="请选择表土回用时间"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Panel>
              </Collapse>

              <Divider />

              <Space>
                <Button 
                  type="primary" 
                  icon={<CalculatorOutlined />} 
                  onClick={handleCalculate}
                  loading={loading}
                  style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                >
                  开始计算
                </Button>
                <Button onClick={handleReset}>
                  重置
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          {result && (
            <>
              <Card title="计算结果" variant="borderless" style={{ marginBottom: '24px' }}>
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="可剥离表土总量"
                      value={result.V1.toFixed(2)}
                      suffix="m³"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="需回用表土总量"
                      value={result.V2.toFixed(2)}
                      suffix="m³"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <Statistic
                      title="盈余/缺口量"
                      value={result.deltaV.toFixed(2)}
                      suffix="m³"
                      valueStyle={{ color: result.deltaV >= 0 ? '#52c41a' : '#cf1322' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="盈余/缺口比例"
                      value={result.deltaV >= 0 ? result.surplusRatio.toFixed(2) : result.deficitRatio.toFixed(2)}
                      suffix="%"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                </Row>

                <Divider />

                <Alert
                  message={getTopsoilStatus(result).title}
                  description={getTopsoilStatus(result).description}
                  type={getTopsoilStatus(result).status}
                  showIcon
                />
              </Card>

              <Card title="详细参数表" variant="borderless" style={{ marginBottom: '24px' }}>
                <Table
                  columns={resultColumns}
                  dataSource={resultData}
                  pagination={false}
                  size="small"
                  bordered
                />
              </Card>

              <Card title="优化建议" variant="borderless">
                {getOptimizationSuggestions(result).map((suggestion, index) => (
                  <Alert
                    key={index}
                    message={suggestion.title}
                    description={suggestion.description}
                    type={suggestion.type}
                    showIcon
                    style={{ marginBottom: index < getOptimizationSuggestions(result).length - 1 ? '12px' : 0 }}
                  />
                ))}
              </Card>
            </>
          )}

          {!result && (
            <Card variant="borderless">
              <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                <CalculatorOutlined style={{ fontSize: '64px', color: '#d9d9d9' }} />
                <div style={{ marginTop: '16px' }}>
                  <Text type="secondary">请输入参数并点击"开始计算"按钮</Text>
                </div>
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default TopsoilCalculator;
